import { reactive } from 'vue'

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>
}

export const pwa = reactive<{
  installable: boolean
  installed: boolean
  updateReady: boolean
}>({
  installable: false,
  installed: window.matchMedia('(display-mode: standalone)').matches,
  updateReady: false,
})

let installPrompt: BeforeInstallPromptEvent | null = null
const CHUNK_RELOAD_WINDOW_MS = 60_000

function initializeChunkLoadRecovery(): void {
  window.addEventListener('vite:preloadError', (event) => {
    const key = `craft-imperium:chunk-reload:${window.location.pathname}${window.location.search}`
    const now = Date.now()
    const previous = Number(window.sessionStorage.getItem(key) ?? '0')
    if (Number.isFinite(previous) && now - previous < CHUNK_RELOAD_WINDOW_MS)
      return

    event.preventDefault()
    window.sessionStorage.setItem(key, String(now))
    window.location.reload()
  })
}

export async function installPwa(): Promise<boolean> {
  if (!installPrompt) return false
  await installPrompt.prompt()
  const choice = await installPrompt.userChoice
  installPrompt = null
  pwa.installable = false
  return choice.outcome === 'accepted'
}

export function initializePwa(): void {
  initializeChunkLoadRecovery()
  window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault()
    installPrompt = event as BeforeInstallPromptEvent
    pwa.installable = true
  })
  window.addEventListener('appinstalled', () => {
    pwa.installed = true
    pwa.installable = false
  })
  if ('serviceWorker' in navigator && import.meta.env.PROD) {
    void navigator.serviceWorker
      .register('/sw.js')
      .then((registration) => {
        registration.addEventListener('updatefound', () => {
          const worker = registration.installing
          worker?.addEventListener('statechange', () => {
            if (
              worker.state === 'installed' &&
              navigator.serviceWorker.controller
            )
              pwa.updateReady = true
          })
        })
      })
      .catch(() => {
        // Registration can fail transiently (offline/private storage policy).
        // PWA support is progressive enhancement and must not leak a rejection.
      })
  }
}
